import React, { useEffect, useRef, useState } from 'react';
import styles from '../ProfessionalEditTabs.module.scss';
import { useAuthContext } from '../../../../../context/AuthContext';
import '../../../../../components/profile-faqs/faq-profile.css';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../../../context/NotificationContext';
import { getProfileDetails } from '../../../../../services/profileService';
import axiosInstance from '../../../../../services/axiosInstance';
import { ProfessionalFaqsPublic } from '../../../../../components/ProfessionalDetail/ProfessionalFaqsPublic';
import { useProfileCompletion } from '../../../../../context/ProfileCompletionContext';

declare global {
    interface Window {
        BodhiwellFaqEditor: any;
        __faqEditorInstance?: any;
    }
}

interface ProfessionalFaqsTabProps {
    editMode: boolean;
}

export const ProfessionalFaqsTab: React.FC<ProfessionalFaqsTabProps> = ({ editMode }) => {
    const faqContainerRef = useRef<HTMLDivElement>(null);
    const editorInstance = useRef<any>(null);
    const { user } = useAuthContext();
    const { t } = useTranslation();
    const { showNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [publicFaqs, setPublicFaqs] = useState<any[]>([]);
    const { checkCompletionStatus } = useProfileCompletion();

    useEffect(() => {
        const container = faqContainerRef.current;
        const loadFaqs = async () => {
            if (!user?.uid) return;
            try {
                if (!window.BodhiwellFaqEditor) {
                    // @ts-ignore
                    await import('../../../../../components/profile-faqs/faq-editor.js');
                }

                const response = await getProfileDetails(user.uid);
                const faqs = response?.faqs?.items || [];
                setPublicFaqs(faqs);

                if (editMode && faqContainerRef.current && window.BodhiwellFaqEditor) {
                    const instance = new window.BodhiwellFaqEditor(faqContainerRef.current, {
                        profileId: user.uid,
                        items: faqs,
                        examples: [
                            t('appAdmin.ProfessionalFaqsTab.example1', '¿En qué consiste una sesión?'),
                            t('appAdmin.ProfessionalFaqsTab.example2', '¿Cuánto dura cada encuentro?'),
                            t('appAdmin.ProfessionalFaqsTab.example3', '¿La atención es presencial, virtual o ambas?'),
                            t('appAdmin.ProfessionalFaqsTab.example4', '¿Necesito alguna preparación previa?'),
                            t('appAdmin.ProfessionalFaqsTab.example5', '¿Cómo se desarrolla el primer encuentro?'),
                            t('appAdmin.ProfessionalFaqsTab.example6', '¿Qué sucede después de reservar?')
                        ]
                    });
                    editorInstance.current = instance;
                    window.__faqEditorInstance = instance;
                }
            } catch (error) {
                console.error("Error loading FAQs", error);
            } finally {
                setLoading(false);
            }
        };

        loadFaqs();

        const handleFaqChange = (e: any) => {
            if (editorInstance.current) {
                window.__faqIsDirty = editorInstance.current.isDirty();
                document.dispatchEvent(new CustomEvent('bodhiwell:faq-change', { detail: e.detail }));
            }
        };

        if (faqContainerRef.current) {
            faqContainerRef.current.addEventListener('bodhiwell:faq-change', handleFaqChange);
        }

        return () => {
            if (container) {
                container.removeEventListener('bodhiwell:faq-change', handleFaqChange);
            }
            if (editorInstance.current) {
                if (typeof editorInstance.current.destroy === 'function') {
                    editorInstance.current.destroy();
                }
                if (container) {
                    container.innerHTML = '';
                }
            }
        };
    }, [editMode, user?.uid, showNotification, t]);

    if (!editMode) {
        if (loading) {
            return (
                <div className={`${styles['edit-tabs__view-mode']} ${styles['edit-tabs__card']}`}>
                    <p>{t('common.loading', 'Cargando...')}</p>
                </div>
            );
        }
        
        if (!publicFaqs || publicFaqs.length === 0 || !publicFaqs.some(f => f.isVisible)) {
            return (
                <div className={`${styles['edit-tabs__view-mode']} ${styles['edit-tabs__card']}`}>
                    <p>{t('appAdmin.ProfessionalFaqsTab.noFaqs', 'No hay preguntas frecuentes para mostrar.')}</p>
                </div>
            );
        }

        return (
            <div className={styles['edit-tabs__view-mode']}>
                <ProfessionalFaqsPublic faqs={publicFaqs} />
            </div>
        );
    }

    return (
        <div className={styles['edit-tabs__form']}>
            <div className="bw-faq-profile">
                {loading && (
                    <div className={styles['edit-tabs__card']}>
                        <p>{t('common.loading', 'Cargando...')}</p>
                    </div>
                )}
                <div ref={faqContainerRef} style={{ display: loading ? 'none' : 'block' }}></div>
            </div>
        </div>
    );
};
